/**
 * Manage Subscription Plans Screen
 * Super Admin can create, edit, and view subscription plans
 */

import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Alert, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Card } from '@app/design-system';
import { subscriptionService, type SubscriptionPlan } from '@app/core';
import { useNavigate } from 'react-router-dom';

export const ManageSubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const isDesktop = width > 768; // Tablet/Desktop breakpoint

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);

  const [formData, setFormData] = useState({
    planName: '',
    subscriptionDays: '',
    startDate: '',
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getSubscriptionPlans({ activeOnly: false });
      setPlans(response);
    } catch (error) {
      console.error('Failed to load plans:', error);
      Alert.alert('Error', 'Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const openPlanModal = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan);
      // Format dates for datetime-local input (YYYY-MM-DDTHH:mm)
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        planName: plan.planName,
        subscriptionDays: plan.subscriptionDays?.toString() || '',
        startDate: formatDateForInput(plan.startDate || ''),
        endDate: formatDateForInput(plan.endDate || ''),
        isActive: plan.isActive
      });
    } else {
      setEditingPlan(null);
      setFormData({
        planName: '',
        subscriptionDays: '',
        startDate: '',
        endDate: '',
        isActive: true
      });
    }
    setShowModal(true);
  };

  const closePlanModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData({
      planName: '',
      subscriptionDays: '',
      startDate: '',
      endDate: '',
      isActive: true
    });
  };

  const handleSavePlan = async () => {
    if (!formData.planName.trim() || !formData.subscriptionDays || !formData.startDate || !formData.endDate) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    // Convert datetime-local format to ISO format
    const formatDateForAPI = (dateString: string) => {
      if (!dateString) return '';
      // If already in ISO format, return as is
      if (dateString.includes('T') && dateString.length > 16) {
        return dateString;
      }
      // Convert from datetime-local format (YYYY-MM-DDTHH:mm) to ISO format
      return dateString + ':00';
    };

    const planData = {
      planName: formData.planName.trim(),
      subscriptionDays: parseInt(formData.subscriptionDays),
      startDate: formatDateForAPI(formData.startDate),
      endDate: formatDateForAPI(formData.endDate),
      isActive: formData.isActive
    };

    setLoading(true);
    try {
      if (editingPlan) {
        await subscriptionService.updateSubscriptionPlan(editingPlan.id, planData);
        Alert.alert('Success', 'Plan updated successfully');
      } else {
        await subscriptionService.createSubscriptionPlan(planData);
        Alert.alert('Success', 'Plan created successfully');
      }
      closePlanModal();
      loadPlans();
    } catch (error: any) {
      console.error('Failed to save plan:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const isWelcomePlan = (planName: string) => {
    return planName.toLowerCase() === 'welcome plan';
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigate('/dashboard')} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Manage Subscription Plans</Text>
        <TouchableOpacity onPress={() => openPlanModal()} style={styles.addButton}>
          <Text style={styles.addButtonText}>+ Add Plan</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {loading && plans.length === 0 ? (
          <ActivityIndicator size="large" color="#007AFF" style={styles.loader} />
        ) : (
          <View style={styles.plansGrid}>
            {plans.map((plan) => (
              <Card key={plan.id} style={styles.planCard}>
                <View style={styles.planHeader}>
                  <Text style={styles.planName}>{plan.planName}</Text>
                  <View style={[styles.statusBadge, plan.isActive && styles.statusBadgeActive]}>
                    <Text style={[styles.statusText, plan.isActive && styles.statusTextActive]}>
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </Text>
                  </View>
                </View>

                <View style={styles.planDetails}>
                  <View style={styles.planDetail}>
                    <Text style={styles.detailLabel}>Duration:</Text>
                    <Text style={styles.detailValue}>{plan.subscriptionDays} days</Text>
                  </View>
                  <View style={styles.planDetail}>
                    <Text style={styles.detailLabel}>Start Date:</Text>
                    <Text style={styles.detailValue}>
                      {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : 'N/A'}
                    </Text>
                  </View>
                  <View style={styles.planDetail}>
                    <Text style={styles.detailLabel}>End Date:</Text>
                    <Text style={styles.detailValue}>
                      {plan.endDate ? new Date(plan.endDate).toLocaleDateString() : 'N/A'}
                    </Text>
                  </View>
                </View>

                <View style={styles.planActions}>
                  <TouchableOpacity
                    onPress={() => openPlanModal(plan)}
                    style={styles.editButton}
                  >
                    <Text style={styles.editButtonText}>
                      Edit
                    </Text>
                  </TouchableOpacity>
                </View>
              </Card>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Modal for Add/Edit Plan */}
      {showModal && (
        <View style={[styles.modalOverlay, isDesktop && styles.modalOverlayDesktop]}>
          <View style={[styles.modalContent, isDesktop && styles.modalContentDesktop]}>
            {/* Fixed Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </Text>
              <TouchableOpacity onPress={closePlanModal} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Scrollable Body */}
            <ScrollView
              style={styles.modalBody}
              contentContainerStyle={styles.modalBodyContent}
              showsVerticalScrollIndicator={true}
            >
              {/* Plan Name */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Plan Name *</Text>
                <TextInput
                  style={[styles.input, editingPlan && isWelcomePlan(editingPlan.planName) && styles.inputDisabled]}
                  value={formData.planName}
                  onChangeText={(text) => setFormData({ ...formData, planName: text })}
                  placeholder="Enter plan name"
                  editable={!editingPlan || !isWelcomePlan(editingPlan.planName)}
                />
              </View>

              {/* Subscription Days */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Subscription Days *</Text>
                <TextInput
                  style={styles.input}
                  value={formData.subscriptionDays}
                  onChangeText={(text) => setFormData({ ...formData, subscriptionDays: text.replace(/[^0-9]/g, '') })}
                  placeholder="Enter subscription days"
                  keyboardType="numeric"
                />
              </View>

              {/* Start Date */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Start Date *</Text>
                <TextInput
                  style={styles.input}
                  value={formData.startDate}
                  onChangeText={(text) => setFormData({ ...formData, startDate: text })}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  // @ts-ignore - type prop works on web
                  type="datetime-local"
                />
              </View>

              {/* End Date */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>End Date *</Text>
                <TextInput
                  style={styles.input}
                  value={formData.endDate}
                  onChangeText={(text) => setFormData({ ...formData, endDate: text })}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  // @ts-ignore - type prop works on web
                  type="datetime-local"
                />
              </View>

              {/* Is Active */}
              <View style={styles.formGroup}>
                <View style={styles.checkboxRow}>
                  <TouchableOpacity
                    onPress={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    style={styles.checkbox}
                  >
                    <View style={[styles.checkboxBox, formData.isActive && styles.checkboxBoxChecked]}>
                      {formData.isActive && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>Active Plan</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>

            {/* Fixed Footer */}
            <View style={styles.modalFooter}>
              <TouchableOpacity onPress={closePlanModal} style={styles.cancelButton}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSavePlan}
                style={[styles.saveButton, loading && styles.saveButtonDisabled]}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>
                    {editingPlan ? 'Update' : 'Create'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0'
  },
  backButton: {
    marginRight: 16
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600'
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#000',
    flex: 1
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#007AFF',
    borderRadius: 8
  },
  addButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14
  },
  scrollView: {
    flex: 1
  },
  scrollContent: {
    padding: 16
  },
  loader: {
    marginVertical: 32
  },
  plansGrid: {
    gap: 16
  },
  planCard: {
    padding: 16
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12
  },
  planName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
    flex: 1
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#f0f0f0'
  },
  statusBadgeActive: {
    backgroundColor: '#E8F5E9'
  },
  statusText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600'
  },
  statusTextActive: {
    color: '#4CAF50'
  },
  planDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20
  },
  planDetails: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 12
  },
  planDetail: {
    flex: 1
  },
  detailLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 4
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000'
  },
  features: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0'
  },
  featuresTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
    marginBottom: 8
  },
  feature: {
    fontSize: 13,
    color: '#666',
    marginBottom: 4,
    lineHeight: 20
  },
  planActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    gap: 8
  },
  editButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#007AFF',
    borderRadius: 6
  },
  editButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14
  },
  buttonDisabled: {
    opacity: 0.5
  },
  // Modal Styles - Mobile First Design
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end', // Mobile: slide from bottom
    alignItems: 'center',
    padding: 0 // Remove padding for mobile
  },
  modalOverlayDesktop: {
    justifyContent: 'center', // Desktop: center modal
    padding: 16
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20, // Mobile: rounded top corners
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 0, // Mobile: no bottom radius
    borderBottomRightRadius: 0,
    width: '100%',
    maxWidth: 600, // Tablet/Desktop: max width
    maxHeight: '95%', // Allow more screen space
    minHeight: '50%', // Ensure minimum height
    display: 'flex',
    flexDirection: 'column',
    // @ts-ignore - boxShadow works on web
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)'
  },
  modalContentDesktop: {
    borderRadius: 12, // Desktop: rounded all corners
    maxHeight: '90%',
    minHeight: 'auto',
    // @ts-ignore - boxShadow works on web
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: 16, // Extra top padding for mobile
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20
  },
  modalTitle: {
    fontSize: 18, // Mobile: slightly smaller
    fontWeight: 'bold',
    color: '#000',
    flex: 1
  },
  closeButton: {
    padding: 8,
    marginRight: -8 // Align to edge
  },
  closeButtonText: {
    fontSize: 28, // Larger for easier tap
    color: '#999',
    lineHeight: 28
  },
  modalBody: {
    flex: 1, // Take available space
    backgroundColor: '#f9f9f9'
  },
  modalBodyContent: {
    padding: 16,
    paddingBottom: 24 // Extra bottom padding
  },
  formGroup: {
    marginBottom: 20 // More spacing on mobile
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    paddingVertical: 14, // Larger tap area on mobile
    fontSize: 16, // Prevent zoom on mobile
    backgroundColor: '#fff',
    minHeight: 48 // Minimum touch target
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999'
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top'
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  checkbox: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44 // Touch target
  },
  checkboxBox: {
    width: 28, // Larger for mobile
    height: 28,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 6,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkboxBoxChecked: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF'
  },
  checkmark: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold'
  },
  checkboxLabel: {
    fontSize: 16, // Larger on mobile
    color: '#333'
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between', // Mobile: full width buttons
    gap: 12,
    padding: 16,
    paddingBottom: 20, // Safe area bottom
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff'
  },
  cancelButton: {
    flex: 1, // Mobile: equal width buttons
    paddingHorizontal: 20,
    paddingVertical: 14, // Larger tap area
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48
  },
  cancelButtonText: {
    color: '#333',
    fontWeight: '600',
    fontSize: 16
  },
  saveButton: {
    flex: 1, // Mobile: equal width buttons
    paddingHorizontal: 20,
    paddingVertical: 14, // Larger tap area
    backgroundColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48
  },
  saveButtonDisabled: {
    opacity: 0.5
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16
  }
});

